import React, { createContext, useContext, ReactNode } from 'react';
import { SnackbarKey } from 'notistack';
import { SnackbarProvider, useSnackbar, VariantType } from 'notistack';

interface NotificationContextProps {
  showNotification: (message: string, variant?: VariantType) => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationProviderInternal = ({ children }: NotificationProviderProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const showNotification = (message: string, variant: VariantType = 'default') => {
    enqueueSnackbar(message, {
      variant,
      autoHideDuration: 5000, // Auto hide after 5 seconds
      preventDuplicate: true, // Prevent duplicate notifications
    });
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  // Create a ref to the notistack's closeSnackbar function
  const notistackRef = React.createRef<any>();
  
  // Function to close a specific snackbar
  const onClickDismiss = (key: SnackbarKey) => () => { 
    notistackRef.current.closeSnackbar(key);
  }
  
  return (
    <SnackbarProvider 
      maxSnack={3} 
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      autoHideDuration={5000} // Default auto hide duration
      hideIconVariant={false} // Show the icon for the variant
      dense // Make the snackbar more compact
      ref={notistackRef}
      // Add a close button to all snackbars
      action={(key) => (
        <button 
          onClick={onClickDismiss(key)}
          style={{ 
            color: '#fff', 
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '4px',
          }}
        >
          ✖
        </button>
      )}
    >
      <NotificationProviderInternal>{children}</NotificationProviderInternal>
    </SnackbarProvider>
  );
};
